/**
 * Phases, plan parsing, jurisdictions and compliance — the data behind setup
 * steps 2, 4 and 5.
 */
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ComplianceRequirement,
  type CountryCode,
  type Jurisdiction,
  type ParsedPhase,
  type ProjectSuggestion,
  type Phase,
  type PhaseInput,
  type RequirementInput,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

export const phaseKeys = {
  list: (projectId: string) => ["project", projectId, "phases"] as const,
};
export const complianceKeys = {
  requirements: ["compliance", "requirements"] as const,
  jurisdiction: (code: string) => ["jurisdictions", code] as const,
};

export function usePhases(projectId: string | undefined) {
  return useQuery({
    queryKey: phaseKeys.list(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<Phase[]> => {
      const res = await api.projects.listPhases({ params: { id: projectId! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.phases;
    },
  });
}

/** Bulk replace. Positions come from array order; the server ignores any sent. */
export function useReplacePhases(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (phases: PhaseInput[]): Promise<Phase[]> => {
      const res = await api.projects.replacePhases({
        params: { id: projectId },
        body: { phases },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.phases;
    },
    onSuccess: (phases) => {
      queryClient.setQueryData(phaseKeys.list(projectId), phases);
    },
  });
}

/** The poll stopped because the screen that started it closed. */
class ParseStopped extends Error {
  constructor() {
    super("The screen that asked for this plan was closed.");
  }
}

/** How often the setup screen asks after a programme being read. */
const PARSE_POLL_MS = 1_000;

/**
 * How long it asks before it gives up. Long enough for a failed attempt and
 * its two retries, which wait 30 seconds and then a minute.
 */
const PARSE_TIMEOUT_MS = 5 * 60_000;

/**
 * Parse a stored programme into phase proposals.
 *
 * The parse runs in the worker (`architecture/0028` §2, 2026-10-03). The
 * start answers with a queued job, and this polls the job until it has
 * succeeded or failed. The mutation stays pending until then, so the screen
 * shows it is busy for the whole wait. A failed job rejects with the job's own
 * words, which the screen shows.
 *
 * **It stops asking when the screen that started it closes** (2026-10-03), as
 * `pollDictation` does. Until then a closed screen went on polling for up to
 * five minutes. A stopped poll rejects with `ParseStopped`, and nobody is
 * left to show it. The job itself runs on in the worker.
 */
export function useParsePlan() {
  const closed = useRef(false);
  useEffect(() => {
    closed.current = false;
    return () => {
      closed.current = true;
    };
  }, []);

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
    }: {
      projectId: string;
      documentId: string;
    }): Promise<{
      phases: ParsedPhase[];
      suggestion?: ProjectSuggestion;
    }> => {
      const res = await api.projects.startPlanParse({
        params: { id: projectId },
        body: { documentId },
      });
      if (res.status !== 202) throw new Error(errorMessage(res));

      const startedAt = Date.now();
      let job = res.body;
      for (;;) {
        if (job.status === "succeeded") {
          return { phases: job.phases ?? [], suggestion: job.suggestion };
        }
        if (job.status === "failed") throw new Error(job.error ?? "That plan couldn’t be read.");
        if (Date.now() - startedAt > PARSE_TIMEOUT_MS) {
          throw new Error("That plan is taking too long to read. The document is saved either way.");
        }
        await new Promise((resolve) => setTimeout(resolve, PARSE_POLL_MS));
        if (closed.current) throw new ParseStopped();
        const poll = await api.projects.planParseStatus({
          params: { id: projectId, jobId: job.jobId },
        });
        if (poll.status !== 200) throw new Error(errorMessage(poll));
        job = poll.body;
      }
    },
  });
}

export function useJurisdiction(code: CountryCode | undefined) {
  return useQuery({
    queryKey: complianceKeys.jurisdiction(code ?? ""),
    enabled: Boolean(code),
    queryFn: async (): Promise<Jurisdiction | null> => {
      const res = await api.jurisdictions.get({ params: { code: code! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.jurisdiction;
    },
  });
}

export function useRequirements() {
  return useQuery({
    queryKey: complianceKeys.requirements,
    queryFn: async (): Promise<ComplianceRequirement[]> => {
      const res = await api.compliance.list();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.requirements;
    },
  });
}

export function useReplaceRequirements() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requirements: RequirementInput[]) => {
      const res = await api.compliance.replaceInfo({ body: { requirements } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.requirements;
    },
    onSuccess: (requirements) => {
      queryClient.setQueryData(complianceKeys.requirements, requirements);
    },
  });
}
